import { useState, useCallback, useRef, useEffect, createElement } from 'react';
import { Play, RefreshCw, ArrowRight, Terminal, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ActionSuggestion } from '@/lib/parseActionSuggestions';

interface ActionSuggestionCardProps {
  action: ActionSuggestion;
  onExecute: (action: ActionSuggestion) => void;
}

const iconMap: Record<ActionSuggestion['type'], typeof Play> = {
  execute_outcome: Play,
  run_workflow: RefreshCw,
  change_phase: ArrowRight,
  send_command: Terminal,
};

const buttonLabelMap: Record<ActionSuggestion['type'], string> = {
  execute_outcome: 'Execute',
  run_workflow: 'Run',
  change_phase: 'Apply',
  send_command: 'Send',
};

export function ActionSuggestionCard({
  action,
  onExecute,
}: ActionSuggestionCardProps): React.ReactElement {
  const [status, setStatus] = useState<'idle' | 'pending' | 'done'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect((): (() => void) => {
    return (): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleClick = useCallback((): void => {
    setStatus('pending');
    try {
      onExecute(action);
    } finally {
      setStatus('done');
      timerRef.current = setTimeout((): void => {
        setStatus('idle');
      }, 2000);
    }
  }, [action, onExecute]);

  const buttonLabel = buttonLabelMap[action.type];

  return (
    <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
      {createElement(iconMap[action.type], { className: 'h-4 w-4 shrink-0 text-primary' })}
      <span className="text-sm text-text-primary flex-1 truncate">{action.label}</span>
      <Button
        size="xs"
        onClick={handleClick}
        disabled={status === 'pending'}
        className={cn(status === 'done' && 'bg-success hover:bg-success/90')}
      >
        {status === 'pending' && <Loader2 className="h-3 w-3 animate-spin" />}
        {status === 'done' && <Check className="h-3 w-3" />}
        {status === 'pending' ? 'Running' : status === 'done' ? 'Done' : buttonLabel}
      </Button>
    </div>
  );
}
